import { useState, useCallback, useMemo } from 'react'
import type { AnalyticsFilter, AnalyticsFilterMode, AnalyticsMode } from '@crm/shared'

const FREE_AXES: AnalyticsFilterMode[] = ['segment', 'city', 'category']

/**
 * Выбирает свободную ось (не текущий mode и не среди фильтров).
 * Используется когда добавляется фильтр через клик по сектору / heatmap.
 */
function pickFreeMode(
  currentMode: AnalyticsMode,
  filters: AnalyticsFilter[],
): AnalyticsFilterMode | null {
  const used = new Set<string>(filters.map((f) => f.mode))
  used.add(currentMode)
  return FREE_AXES.find((m) => !used.has(m)) ?? null
}

/**
 * Локальный state круговой аналитики и heatmap. Живёт пока открыт дашборд,
 * не персистится (по требованию). Селекция менеджеров берётся отдельно
 * из usePlanningSelection (общая с таблицами /planning).
 */
export function useClientsAnalyticsState() {
  // Дефолт «Сегмент»: при выборе менеджера сразу видно распределение его
  // клиентов по сегментам, центр donut — количество клиентов.
  const [mode, setMode] = useState<AnalyticsMode>('segment')
  const [filters, setFilters] = useState<AnalyticsFilter[]>([])

  const addFilter = useCallback(
    (m: AnalyticsFilterMode, value: string) => {
      setFilters((prev) => {
        const next = prev.filter((f) => f.mode !== m)
        next.push({ mode: m, value })
        return next
      })
      setMode((curMode) => {
        // Если текущий режим донат — фильтрующее поле, переключаемся на свободную ось.
        if (curMode !== 'matrix' && curMode === m) {
          const next: AnalyticsFilter[] = [
            ...filters.filter((f) => f.mode !== m),
            { mode: m, value },
          ]
          const free = pickFreeMode(curMode, next)
          return free ?? curMode
        }
        return curMode
      })
    },
    [filters],
  )

  const removeFilter = useCallback((m: AnalyticsFilterMode) => {
    setFilters((prev) => prev.filter((f) => f.mode !== m))
  }, [])

  const clearFilters = useCallback(() => setFilters([]), [])

  const drilldownDisabled = useMemo(() => {
    // Все 3 оси заняты — кликать сектор бессмысленно.
    if (mode === 'matrix') return false
    const used = new Set<string>(filters.map((f) => f.mode))
    used.add(mode)
    return FREE_AXES.every((m) => used.has(m))
  }, [mode, filters])

  return {
    mode,
    setMode,
    filters,
    addFilter,
    removeFilter,
    clearFilters,
    drilldownDisabled,
    pickFree: () => pickFreeMode(mode, filters),
  }
}

export type ClientsAnalyticsState = ReturnType<typeof useClientsAnalyticsState>
